import { useRef } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"

import { buttonFilled } from "css/buttons"
import { contrastMode } from "css/a11y"
import { fullGrid } from "css/grid"
import { legend100, surtitle70 } from "css/text"

import generateId from "utils/generateId"

import VideoLauncher from "components/video-launcher/VideoLauncher"
import OysterStoryLogo from "components/oyster-story-logo/OysterStoryLogo"
import { cssVars } from "components/nav-v2/Wrapper"
import Video, { cssContainer } from "components/media/VideoCLD"
import { pageHeaderCSS, kickerCN, subtitleCN } from "components/page-heading/style"
import WatermarkOyster from "components/oyster-story-logo/Watermark"
import { Section as TxtFillSection } from "components/text-filling/TextFilling"
import ScrollInvit from "components/scroll-invit/ScrollInvit"
import getMediaQuery from "css/breakpoints"

const Section = styled.section`
  ${fullGrid}
  grid-template-rows: 100vh;
  position: relative;
  justify-content: center;
  z-index: 0;
  height: 200vh;
  margin-block-end: -100vh;

  & > div.${cssContainer} {
    position: sticky;
    top: 0;
    width: 100%;
    height: 100vh;
    grid-column: doc;
    grid-row: 1;

    z-index: -1;
  }

  & > div.${cssContainer} img,
  & > div.${cssContainer} video {
    width: 100%;
    height: 100vh;
    object-fit: cover;
  }

  & + ${TxtFillSection} {
    background-color: white;
    border-top-right-radius: 30px;
    border-top-left-radius: 30px;
  }

  & > svg {
    position: sticky;
    grid-column: doc;
    grid-row: 1;
    height: calc(94vh - var(${cssVars.main.safe}));
    margin-inline-start: 2vh;
    align-self: center;
    margin-block-start: var(${cssVars.main.safe});
    top: calc(var(${cssVars.main.safe}) + 3vh);
  }
`

const ChapterNum = styled.p`
  ${surtitle70}
  text-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  grid-row: 1;
  grid-column: doc;
  justify-self: center;
  margin-block-start: calc(var(${cssVars.main.safe}) + 20px);
  position: relative;
  height: fit-content;
  ${contrastMode}
`

const Header = styled(motion.header)`
  ${pageHeaderCSS}
  margin-top: 0;
  grid-template-rows: [kicker subtitle] min-content [mid-line] min-content [title video-cta];
  position: relative;
  top: calc((100vh - var(${cssVars.main.safe})) * 0.44);
  height: 0;

  margin-block-start: calc(-1 * var(${cssVars.main.safe}));

  > button,
  .cta-cont button {
    ${buttonFilled}
  }

  > button,
  & .cta-cont {
    grid-row: video-cta;
    grid-column: main;
    justify-self: center;
    margin-block-start: 50px;
  }

  & h1 .txttranslation {
    ${legend100}
    display:block;
  }

  & h1,
  & p {
    position: relative;
    text-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
    ${contrastMode}
  }

  ${getMediaQuery("l")} {
    & h1 {
      grid-column: col 3 / span 8;
    }
  }

  & h1 > svg {
    margin-block-start: 0.6rem;
    width: calc(var(--grid-col-unit) * 5);
    filter: drop-shadow(0px 0px 12px rgba(0, 0, 0, 0.05));
  }
`

function PageTitleBlock({ title, subtitle, kicker, videoCTA, use_os_logo, animated_logo, logo_translation, theme = "dark-theme" }) {
  const console = useConsole()

  const titleId = generateId("title")

  const variants = {
    show: { opacity: 1, y: 0, transition: { ease: "easeOut", duration: 0.7 } },
    hide: { opacity: 0, y: 30 },
  }

  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")

  return (
    <Header className={theme} initial='hide' animate='show' transition={{ staggerChildren: 0.45 }}>
      {kicker ? (
        animated_logo && !prefersReducedMotion ? (
          <motion.p className={kickerCN} variants={variants}>
            {kicker}
          </motion.p>
        ) : (
          <p className={kickerCN}>{kicker}</p>
        )
      ) : null}
      {use_os_logo ? (
        <h1 id={titleId}>
          <OysterStoryLogo animated_logo={animated_logo} />
          {logo_translation ? <span className='txttranslation'>{logo_translation}</span> : null}
        </h1>
      ) : (
        <h1 dangerouslySetInnerHTML={{ __html: title }} id={titleId} />
      )}
      {subtitle && <p className={subtitleCN}>{subtitle}</p>}
      {!!videoCTA && animated_logo && !prefersReducedMotion ? (
        <motion.div className='cta-cont' variants={variants}>
          <VideoLauncher {...videoCTA} titleId={titleId} />
        </motion.div>
      ) : !!videoCTA ? (
        <VideoLauncher {...videoCTA} titleId={titleId} />
      ) : null}
    </Header>
  )
}

export default function CoverIntroFixedVideo({
  heading,
  video_cld: video,
  video_amp_cta,
  className,
  theme,
  use_os_logo,
  animated_logo,
  logo_translation,
  chapter,
  watermark,
  scrollinvit,
}) {
  const console = useConsole()

  const videoCTA = video_amp_cta.length > 1 ? video_amp_cta[1] : video_amp_cta[0]

  const ref = useRef()
  const vidRef = useRef()

  return (
    <>
      <PageTitleBlock
        {...heading}
        videoCTA={videoCTA}
        theme={theme ? theme?.theme : "dark-theme"}
        use_os_logo={use_os_logo}
        logo_translation={logo_translation}
        animated_logo={animated_logo}
        isTitleFixed={false}
      />
      <Section ref={ref} className={`${className} ${theme}`}>
        {video && <Video ref={vidRef} autoPlay loop noButton {...video} sizes='100vw' thresholdValue='0.5' />}
        {chapter ? <ChapterNum>{chapter}</ChapterNum> : null}
        {watermark ? <WatermarkOyster /> : null}
        {scrollinvit ? <ScrollInvit /> : null}
      </Section>
    </>
  )
}
